import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Quote as QuoteIcon } from "lucide-react";
import { useWorld } from "@/context/WorldContext";
import { RichContent } from "@/components/RichContent";
import { Portrait } from "@/components/ShipBits";
import { roleLabel } from "@/lib/family";
import { blockEmpty, quoteEmpty, relEntries, relKey, TOP_IDS, uniqTropes } from "@/lib/blocks";

export const Ref = ({ r, className = "" }) => {
  const { pages, basePath } = useWorld();
  const p = r?.page_id && pages.find((x) => x.id === r.page_id);
  if (p) return <Link to={`${basePath}/p/${p.id}`} className={`text-amber-300 hover:text-amber-200 transition-colors ${className}`}>{p.title}</Link>;
  return r?.text ? <span className={className}>{r.text}</span> : null;
};

export const QuoteView = ({ q, big = false, testId }) => {
  const lines = q.lines.filter((l) => l.text.trim());
  const script = lines.length > 1;
  return (
    <figure className={big ? "relative pl-10 py-2" : "rounded-xl border border-line bg-panel/60 px-5 py-4"} data-testid={testId}>
      {big && <QuoteIcon className="absolute left-0 top-1 w-7 h-7 text-amber-500/40" />}
      <blockquote className={script ? "space-y-2" : ""}>
        {lines.map((l, i) => (
          <div key={i} className={script ? "" : ""}>
            {script && <p className="font-code text-[11px] uppercase tracking-[0.2em] text-amber-400/80">{l.speaker?.text || l.speaker?.page_id ? <Ref r={l.speaker} /> : "—"}</p>}
            <p className={`font-display italic ${big ? "text-2xl md:text-3xl text-amber-50 leading-snug" : "text-xl text-slate-200"} ${script ? "pl-5" : ""}`}>“{l.text}”</p>
          </div>
        ))}
      </blockquote>
      {(!script && (lines[0]?.speaker?.text || lines[0]?.speaker?.page_id)) || q.said_to?.text || q.said_to?.page_id ? (
        <figcaption className="mt-2 text-sm text-slate-400">
          {!script && (lines[0].speaker?.text || lines[0].speaker?.page_id) && <>— <Ref r={lines[0].speaker} /></>}
          {(q.said_to?.text || q.said_to?.page_id) && <span className="text-slate-500">{!script && (lines[0].speaker?.text || lines[0].speaker?.page_id) ? " · " : ""}said to <Ref r={q.said_to} /></span>}
        </figcaption>
      ) : null}
    </figure>
  );
};

const Journey = ({ b }) => {
  const tabs = b.tabs.filter((t) => t.html && t.html.replace(/<[^>]+>/g, "").trim());
  const [at, setAt] = useState(0);
  const cur = tabs[Math.min(at, tabs.length - 1)];
  return (
    <div data-testid={`journey-${b.id}`}>
      <div className="flex flex-wrap gap-1 border-b border-line mb-4">
        {tabs.map((t, i) => (
          <button key={i} onClick={() => setAt(i)} data-testid={`journey-tab-${i}`}
            className={`px-4 py-2 text-sm -mb-px border-b-2 transition-colors ${t === cur ? "border-amber-400 text-amber-200" : "border-transparent text-slate-400 hover:text-slate-100"}`}>{t.name}</button>
        ))}
      </div>
      <div key={at} className="rise"><RichContent html={cur.html} /></div>
    </div>
  );
};

const Timeline = ({ b }) => {
  const { pages, basePath } = useWorld();
  return (
    <ol className="relative border-l-2 border-amber-500/40 ml-2 space-y-6" data-testid={`char-timeline-${b.id}`}>
      {b.points.map((p, i) => {
        const ev = p.event_id && pages.find((x) => x.id === p.event_id);
        return (
          <li key={i} className="pl-6 relative rise" style={{ animationDelay: `${i * 50}ms` }} data-testid={`char-timeline-point-${i}`}>
            <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-amber-400 shadow-[0_0_12px_rgba(245,158,11,0.7)]" />
            {p.date && <p className="font-code text-xs text-amber-300/90">{p.date}</p>}
            <p className="font-display text-xl text-amber-50">{p.title}</p>
            {ev && <Link to={`${basePath}/p/${ev.id}`} className="inline-flex items-center gap-1 mt-1 text-xs rounded-full border border-rose-500/40 bg-rose-500/10 text-rose-200 px-2 py-0.5 hover:bg-rose-500/20" data-testid={`char-timeline-event-${i}`}><CalendarClock className="w-3 h-3" /> {ev.title}</Link>}
            {p.description && <p className="text-sm text-slate-400 mt-1 whitespace-pre-line">{p.description}</p>}
          </li>
        );
      })}
    </ol>
  );
};

const Relationships = ({ b, page }) => {
  const { pages } = useWorld();
  return (
    <div className="space-y-6" data-testid={`relationships-${b.id}`}>
      {relEntries(page).map((g) => (
        <div key={g.group}>
          <h4 className="font-code text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">{g.group}</h4>
          <div className="space-y-4">
            {g.items.map((it, i) => {
              const note = b.notes.find((n) => n.group === g.group && n.key === relKey(it));
              const p = it.page_id && pages.find((x) => x.id === it.page_id);
              return (
                <div key={i} className="flex gap-3" data-testid={`rel-entry-${g.group}-${i}`}>
                  <Portrait page={p || { title: it.text }} size="w-10 h-10 text-xs" />
                  <div className="min-w-0 flex-1">
                    <p className="text-slate-100"><Ref r={it} className="font-display text-lg" />{it.role && <span className="ml-2 text-xs font-code text-slate-500">{roleLabel(it.role)}</span>}</p>
                    {note?.html && <RichContent html={note.html} />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

const BODY = {
  text: ({ b }) => <RichContent html={b.html} />,
  journey: Journey, timeline: Timeline, relationships: Relationships,
  tropes: ({ b }) => <div className="flex flex-wrap gap-2">{uniqTropes(b.tropes).map((t) => <span key={t.toLowerCase()} className="text-sm rounded-full border border-amber-500/30 bg-amber-500/5 text-amber-100 px-3 py-1" data-testid="trope-chip">{t}</span>)}</div>,
  quotes: ({ b }) => <div className="space-y-3">{b.quotes.filter((q) => !quoteEmpty(q)).map((q, i) => <QuoteView key={i} q={q} testId={`quote-${i}`} />)}</div>,
};

const Section = ({ b, page }) => {
  const Body = BODY[b.kind];
  return (
    <section id={`sec-${b.id}`} className="rise scroll-mt-24 min-w-0" data-testid={`section-${b.id}`}>
      <h2 className="font-display text-3xl text-amber-100 pb-2 mb-4 border-b border-amber-500/25">{b.title}</h2>
      <Body b={b} page={page} />
    </section>
  );
};

export const PageSections = ({ page }) => {
  const shown = (page.blocks || []).filter((b) => !blockEmpty(b, page));
  const [general, pair, rest] = [shown.filter((b) => b.id === "general"), shown.filter((b) => TOP_IDS.slice(1).includes(b.id)), shown.filter((b) => !TOP_IDS.includes(b.id))];
  const pinned = page.pinned_quote && !quoteEmpty(page.pinned_quote);
  if (!shown.length && !pinned) return null;
  return (
    <div className="space-y-12" data-testid="page-sections">
      {shown.length > 1 && (
        <nav className="flex flex-wrap gap-x-4 gap-y-1 text-sm" data-testid="page-contents">
          <span className="font-code text-[10px] uppercase tracking-[0.25em] text-slate-500 pt-1">Contents</span>
          {shown.map((b) => <a key={b.id} href={`#sec-${b.id}`} className="text-slate-400 hover:text-amber-300 transition-colors" data-testid={`contents-${b.id}`}>{b.title}</a>)}
        </nav>
      )}
      {pinned && <QuoteView q={page.pinned_quote} big testId="pinned-quote" />}
      {general.map((b) => <Section key={b.id} b={b} page={page} />)}
      {pair.length > 0 && <div className={`grid gap-10 ${pair.length > 1 ? "md:grid-cols-2" : ""}`}>{pair.map((b) => <Section key={b.id} b={b} page={page} />)}</div>}
      {rest.map((b) => <Section key={b.id} b={b} page={page} />)}
    </div>
  );
};
